import { useState } from 'react';
import { AlertTriangle, XCircle } from 'lucide-react';
import { ApiError, AdhocConflictResponse, schedulesApi } from '@/lib/api';

// Shared button classes so the website ad-hoc controls match the on-host agent panel
// exactly (purple start, neutral cancel, red stop) in both light and dark.
export const BTN_START = 'rounded-lg bg-purple-700 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-purple-600 disabled:opacity-60';
export const BTN_CANCEL = 'rounded-lg bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-200 disabled:opacity-60 dark:bg-zinc-800 dark:text-slate-300 dark:hover:bg-zinc-700';
export const BTN_STOP = 'rounded-lg bg-red-700 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-red-600 disabled:opacity-60';

// Website ad-hoc (unscheduled) start. Deliberately mirrors the on-host agent panel's
// UnscheduledModal (same title/subtitle, field set + order, "Private Stream" toggle,
// and "▶ Start Stream" action) so operators see the same tool on both surfaces.
const INPUT = 'w-full rounded border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 placeholder-gray-400 focus:border-blue-500 focus:outline-none dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100';

export default function AdHocStreamModal({ facilityId, sheetName, onClose, onStarted }: {
  facilityId: string;
  sheetName: string;
  onClose: () => void;
  onStarted?: () => void;
}) {
  const [eventName, setEventName] = useState('');
  const [team1Name, setTeam1Name] = useState('');
  const [team2Name, setTeam2Name] = useState('');
  const [obsSceneName, setObsSceneName] = useState('');
  const [notes, setNotes] = useState('');
  const [duration, setDuration] = useState('');
  const [isPrivate, setIsPrivate] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  // Scheduled games this stream would block, as reported by the server. While
  // set, the form gives way to a decision: pause them, start anyway, or go back
  // (e.g. to set a Duration that finishes in time).
  const [conflict, setConflict] = useState<AdhocConflictResponse | null>(null);

  const timeIn = (iso: string, tz: string) => {
    try { return new Date(iso).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', timeZone: tz }); }
    catch { return new Date(iso).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }); }
  };

  async function submit(conflictAction?: 'pause' | 'ignore') {
    if (!eventName.trim()) { setErr('Event name is required'); return; }
    // Blank is the default and means "no fixed duration" — stop it yourself.
    const mins = duration.trim() ? Number(duration.trim()) : undefined;
    if (mins !== undefined && (!Number.isInteger(mins) || mins < 1 || mins > 720)) {
      setErr('Duration must be a whole number of minutes, from 1 to 720 (12 hours)'); return;
    }
    setErr(null);
    setLoading(true);
    try {
      await schedulesApi.sendAgentCommand({
        facilityId,
        type: 'start_adhoc',
        sheetName,
        eventName: eventName.trim(),
        ...(team1Name.trim() ? { team1Name: team1Name.trim() } : {}),
        ...(team2Name.trim() ? { team2Name: team2Name.trim() } : {}),
        ...(obsSceneName.trim() ? { obsSceneName: obsSceneName.trim() } : {}),
        ...(notes.trim() ? { notes: notes.trim() } : {}),
        privacyStatus: isPrivate ? 'unlisted' : 'public',
        ...(mins !== undefined ? { durationMinutes: mins } : {}),
        ...(conflictAction ? { conflictAction } : {}),
      });
      onStarted?.();
      onClose();
    } catch (e) {
      const data = e instanceof ApiError ? e.data : undefined;
      if (e instanceof ApiError && e.status === 409 && data?.['code'] === 'ADHOC_SCHEDULE_CONFLICT') {
        setConflict(data as unknown as AdhocConflictResponse);
        return;
      }
      setErr(e instanceof Error ? e.message : 'Could not start the stream');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="w-full max-w-md overflow-hidden rounded-lg bg-white shadow-xl dark:bg-gray-900" onClick={e => e.stopPropagation()}>
        {/* Header — matches the on-host modal */}
        <div className="flex items-start justify-between border-b border-gray-200 px-5 py-4 dark:border-gray-700">
          <div>
            <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">Unscheduled Stream</h3>
            <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">Sheet {sheetName} — not linked to a CSM schedule</p>
          </div>
          <button onClick={onClose} className="rounded p-1 text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800"><XCircle className="h-5 w-5" /></button>
        </div>

        {conflict ? (
          <div className="space-y-4 p-5">
            <div className="flex gap-2.5 rounded-md border border-amber-300 bg-amber-50 px-3 py-2.5 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-200">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <div>
                <p className="font-medium">
                  {conflict.conflicts.length === 1 ? 'A scheduled game' : `${conflict.conflicts.length} scheduled games`} on
                  sheet {sheetName} would not stream.
                </p>
                <p className="mt-0.5 text-xs text-amber-800 dark:text-amber-300">
                  A sheet streams one thing at a time, and this stream would still be running
                  {conflict.untilEndOfDay ? ' (it has no Duration, so it runs until someone stops it)' : ` until ${timeIn(conflict.until, conflict.timezone)}`}.
                </p>
              </div>
            </div>
            <ul className="space-y-1 text-sm text-gray-800 dark:text-gray-200">
              {conflict.conflicts.map(c => (
                <li key={c.id} className="flex justify-between gap-3 rounded border border-gray-200 px-3 py-1.5 dark:border-gray-700">
                  <span className="min-w-0 truncate">
                    {c.eventName}
                    {c.team1Name && c.team2Name && <span className="text-gray-500 dark:text-gray-400"> · {c.team1Name} vs {c.team2Name}</span>}
                  </span>
                  <span className="shrink-0 text-gray-600 dark:text-gray-400">{timeIn(c.scheduledStart, conflict.timezone)}</span>
                </li>
              ))}
            </ul>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Paused {conflict.conflicts.length === 1 ? 'game comes' : 'games come'} back automatically when this stream
              stops, as long as {conflict.conflicts.length === 1 ? 'it hasn’t' : 'they haven’t'} started yet. Or go back
              and set a Duration that ends before {timeIn(conflict.conflicts[0]!.scheduledStart, conflict.timezone)}.
            </p>
            {err && <p className="text-sm text-red-600 dark:text-red-400">{err}</p>}
            <div className="flex flex-col gap-2">
              <button type="button" className={BTN_START} disabled={loading} onClick={() => submit('pause')}>
                {loading ? 'Starting…' : `Pause ${conflict.conflicts.length === 1 ? 'it' : 'them'} and start`}
              </button>
              <button type="button" className={BTN_CANCEL} disabled={loading} onClick={() => submit('ignore')}>
                Start anyway — {conflict.conflicts.length === 1 ? 'that game' : 'those games'} won’t stream
              </button>
              <button type="button" className={BTN_CANCEL} disabled={loading} onClick={() => { setConflict(null); setErr(null); }}>
                Back
              </button>
            </div>
          </div>
        ) : (
        <div className="space-y-4 p-5">
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">Event Name <span className="text-red-500">*</span></label>
            <input className={INPUT} value={eventName} onChange={e => setEventName(e.target.value)} placeholder="e.g. Club Practice — Sheet A" autoFocus />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">Team 1</label>
              <input className={INPUT} value={team1Name} onChange={e => setTeam1Name(e.target.value)} placeholder="Optional" />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">Team 2</label>
              <input className={INPUT} value={team2Name} onChange={e => setTeam2Name(e.target.value)} placeholder="Optional" />
            </div>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">OBS Scene</label>
            <input className={INPUT} value={obsSceneName} onChange={e => setObsSceneName(e.target.value)} placeholder="Optional — leave blank for current scene" />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">Notes</label>
            <textarea className={`${INPUT} resize-none`} rows={2} value={notes} onChange={e => setNotes(e.target.value)} placeholder="Optional" />
          </div>

          <div>
            <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">Duration</label>
            <input
              className={INPUT}
              type="number"
              min={1}
              max={720}
              step={5}
              value={duration}
              onChange={e => setDuration(e.target.value)}
              placeholder="Minutes — leave blank to stop it yourself"
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Stops the stream automatically this many minutes after it goes live.
            </p>
          </div>

          {/* Private Stream — same semantics/copy as the on-host modal */}
          <label className="flex cursor-pointer items-start gap-2.5 rounded-md border border-gray-200 bg-gray-50 px-3 py-2.5 dark:border-gray-700 dark:bg-gray-800/50">
            <input type="checkbox" checked={isPrivate} onChange={e => setIsPrivate(e.target.checked)} className="mt-0.5 h-4 w-4 accent-purple-600" />
            <span className="text-sm">
              <span className="font-medium text-gray-800 dark:text-gray-200">Private Stream</span>
              <span className="mt-0.5 block text-xs text-gray-500 dark:text-gray-400">Not listed publicly — share the link with coaches.</span>
            </span>
          </label>

          {err && <p className="text-sm text-red-600 dark:text-red-400">{err}</p>}

          <div className="flex gap-3 pt-1">
            <button type="button" className={`flex-1 ${BTN_CANCEL}`} onClick={onClose} disabled={loading}>Cancel</button>
            <button type="button" className={`flex-1 ${BTN_START}`} onClick={() => submit()} disabled={loading}>
              {loading ? 'Starting…' : '▶ Start Stream'}
            </button>
          </div>
        </div>
        )}
      </div>
    </div>
  );
}
